import * as React from 'react';
import { Popover, Tooltip, Typography } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import styles from './new-post-modal.module.scss';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseFullscreenOutlinedIcon from '@mui/icons-material/CloseFullscreenOutlined';
import EmojiPicker, { EmojiClickData, EmojiStyle } from 'emoji-picker-react';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import { useEffect, useState } from 'react';
import YouTubeIcon from '@mui/icons-material/YouTube';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ChipInput from '../../organisms/chip-input/chip-input';
import { TArticle, TChannel, TTopic } from '../../../models/common';
import NewPostTextFiled from '../new-post-text-filed/new-post-text-filed';
import Input from 'components/molecules/input/input';
import Button from 'components/atoms/button/button';
import { WMenu } from 'components/wrappers/wrapper';
import NavButton from 'components/molecules/nav-button/nav-button';
import useDeviceType from 'custom-hooks/useDeviceType';

interface NewPostProps {
  textFields: string[];
  alignments: string[];
  images: string[];
  selectedChannel: TChannel;
  topics: TTopic[];
  article: TArticle;
  channels: TChannel[];
  onClose: () => void;
  onClickYoutube: () => void;
  onClickUploadImage: () => void;
  onClickUploadCoverImage: () => void;
  onPublish: () => void;
  onChangeData: (data: TArticle) => void;
  onChangeChannel: (channel: TChannel) => void;
  onChangeActiveTextFiled: (index: number) => void;
  onChangeAlignments: (alignments: string[]) => void;
  onChangeTextFileds: (value: string[]) => void;
  onChooseEmoji: (emojiObj: EmojiClickData) => void;
}

const NewPostModal: React.FC<NewPostProps> = ({
  textFields,
  alignments,
  images,
  selectedChannel,
  topics,
  article,
  channels,
  onClose,
  onClickYoutube,
  onClickUploadImage,
  onClickUploadCoverImage,
  onPublish,
  onChangeData,
  onChangeChannel,
  onChangeActiveTextFiled,
  onChangeAlignments,
  onChangeTextFileds,
  onChooseEmoji,
}) => {
  const { isDesktop } = useDeviceType();

  const handleChange = (index: number, value: string) => {
    const updatedTextFields = [...textFields];
    updatedTextFields[index] = value;
    onChangeTextFileds(updatedTextFields);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = (page?: TChannel) => {
    setAnchorEl(null);
    if (!page) return;
    onChangeChannel(page);
    setSelectedTopics([]);
  };
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const openEmoji = Boolean(anchor);

  const handleCloseEmoji = () => {
    setAnchor(null);
  };
  const handleClickEmo = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchor(event.currentTarget);
  };

  const [fullScreen, setFullScreen] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);

  useEffect(() => {
    setFullScreen(isDesktop ? false : true);
  }, [isDesktop]);

  const handleFullScreenClick = () => {
    setFullScreen(!fullScreen);
  };

  useEffect(() => {
    onChangeData({ topics: getTopicsIds() });
  }, [selectedTopics]);

  const getTopicsIds = () => {
    return selectedTopics.map((el) => {
      return topics.find((t) => t.title === el)?._id as string;
    });
  };

  const handleDeleteChip = (value: string) => {
    const _topics = selectedTopics.filter((el) => el !== value);
    setSelectedTopics(_topics);
  };

  const topicOptions = () => {
    const titles = Array.from(topics, (t) => String(t.title));
    return titles.filter((el) => !selectedTopics.includes(el));
  };

  const renderPostingButton = () => (
    <Button type="base" onClick={handleClick} className={styles.postInButton}>
      <div className={styles.postInButtonContent}>
        <span>Posting in {selectedChannel?.title!}</span> <ExpandMoreIcon fontSize="small" />
      </div>
    </Button>
  );

  return (
    <div className={styles.newPost}>
      <div className={`${styles.container} ${styles[fullScreen ? 'full' : 'mini']}`}>
        <div className={styles.header}>
          <Typography variant="h5" className={styles.text}>
            Create post
          </Typography>
          <div className={styles.icons}>
            <Tooltip title="Cover Image" onClick={onClickUploadCoverImage}>
              <AddPhotoAlternateIcon fontSize="small" />
            </Tooltip>
            {isDesktop && (
              <Tooltip
                title={fullScreen ? 'Exit Full Screen' : 'Full Screen'}
                onClick={handleFullScreenClick}
              >
                {fullScreen ? (
                  <CloseFullscreenOutlinedIcon fontSize="small" />
                ) : (
                  <OpenInFullIcon fontSize="small" />
                )}
              </Tooltip>
            )}
            <Tooltip title="Close" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </Tooltip>
          </div>
        </div>
        <div className={styles.content}>
          {article.cover_image && (
            <img src={article.cover_image} className={styles['cover-image']} alt="cover" />
          )}
          <Input
            value={article.title!}
            placeholder="Title"
            onChange={(e) => onChangeData({ title: e.target.value })}
            className={styles.titleInput}
            onFocus={() => onChangeActiveTextFiled(-1)}
          />
          {article.youtube_id && (
            <iframe
              title="youtube-video"
              className={styles['youtube-video']}
              src={`https://www.youtube.com/embed/${article.youtube_id}`}
            />
          )}
          {textFields.map((value, index) => (
            <NewPostTextFiled
              key={index}
              images={images}
              index={index}
              value={value}
              alignments={alignments}
              onChangeAlignments={onChangeAlignments}
              onChangeActiveTextFiled={onChangeActiveTextFiled}
              onChangeText={(value) => handleChange(index, value)}
            />
          ))}
        </div>

        <div className={styles.footer}>
          {!isDesktop && <div className={styles.posting}>{renderPostingButton()}</div>}
          <ChipInput
            limit={5}
            options={topicOptions()}
            chips={selectedTopics}
            type="autocomplete"
            placehodler="Choose up to 5 topics"
            addItem={(value) => setSelectedTopics([...selectedTopics, value])}
            deleteItem={(value) => handleDeleteChip(value)}
            optionsClass={styles.customOptions}
          />

          <div className={styles.bottom}>
            <div className={styles.left}>
              <Tooltip title="Add image" onClick={onClickUploadImage}>
                <ImageOutlinedIcon fontSize="small" />
              </Tooltip>
              <Tooltip title="Add emoji" onClick={handleClickEmo}>
                <EmojiEmotionsOutlinedIcon fontSize="small" />
              </Tooltip>
              <Tooltip title="Attach video" onClick={onClickYoutube}>
                <YouTubeIcon />
              </Tooltip>
              <Popover
                id={'simple-popover'}
                open={openEmoji}
                anchorEl={anchor}
                onClose={handleCloseEmoji}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <EmojiPicker
                  onEmojiClick={onChooseEmoji}
                  autoFocusSearch={false}
                  emojiStyle={EmojiStyle.NATIVE}
                />
              </Popover>
            </div>
            <div className={styles.right}>
              {isDesktop && renderPostingButton()}
              <WMenu
                id="post-menu"
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={() => handleMenuClose()}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                className={styles.channelMenu}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      border: 'var(--border)',
                      padding: '1rem 0',
                      width: '12rem',
                      borderRadius: 'var(--border-radius)',
                      background: 'var(--background)'
                    },
                  },
                }}
              >
                {channels.map((page, index) => (
                  <NavButton
                    key={index}
                    title={page.title!}
                    icon={page.pageicon!}
                    onClick={() => handleMenuClose(page)}
                    className={styles.menuItem}
                  />
                ))}
              </WMenu>

              <Button disabled={!article.title?.length} onClick={onPublish} radius="10rem">
                Publish
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewPostModal;
